import React from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { Cropper as AdvancedCropper, type CropperRef } from 'react-advanced-cropper';
import PerspectiveCropperCanvas from '../PerspectiveCropperCanvas/PerspectiveCropperCanvas';
import type { PerspectiveCropperHandle } from '../../../types/perspectiveCropper';
import FreeCropCanvas from '../FreeCropCanvas/FreeCropCanvas';
import type { FreeCropHandle } from '../../../types/freeCrop';

type EditReceiptPreviewPanelProps = {
  previewUrl: string;
  loading: boolean;
  modeSwitching: boolean;
  editMode: 'tilt' | 'perspective' | 'free';
  perspectiveKey: number;
  rotation: number;
  selectedFile: File | null;
  perspectiveLoading: boolean;
  cropperRef: React.MutableRefObject<CropperRef | null>;
  perspectiveCropperRef: React.MutableRefObject<PerspectiveCropperHandle | null>;
  freeCropRef: React.MutableRefObject<FreeCropHandle | null>;
  onCropperChange: (cropper: CropperRef) => void;
  onPerspectiveState: (state: { loading?: boolean }) => void;
};

const EditReceiptPreviewPanel: React.FC<EditReceiptPreviewPanelProps> = React.memo(({
  previewUrl,
  loading,
  modeSwitching,
  editMode,
  perspectiveKey,
  rotation,
  selectedFile,
  perspectiveLoading,
  cropperRef,
  perspectiveCropperRef,
  freeCropRef,
  onCropperChange,
  onPerspectiveState,
}) => {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          minHeight: '90%',
          height: '100%',
          alignSelf: 'stretch',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: previewUrl ? 'flex-start' : 'center',
          alignItems: 'center',
          textAlign: 'center',
          bgcolor: '#2C6CA00A',
        }}
      >
        {!previewUrl && (
          loading ? (
            <Box sx={{ display: 'flex', flex: '1 1 auto', width: '100%', minHeight: 0, justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                Loading image...
              </Typography>
            </Box>
          ) : (
            <Alert severity="warning">No image available for this receipt.</Alert>
          )
        )}
        {previewUrl && (
          <>
            {modeSwitching && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Applying changes...
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flex: '1 1 auto', width: '100%', minHeight: 0 }}>
              {editMode === 'tilt' && (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    flex: '1 1 auto',
                    minHeight: 0,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <AdvancedCropper
                    key={`tilt-${perspectiveKey}-${rotation}`}
                    ref={cropperRef}
                    src={previewUrl}
                    className="rac-cropper"
                    onChange={onCropperChange}
                    defaultTransforms={{
                      rotate: rotation,
                    }}
                    defaultSize={{
                      width: 4096,
                      height: 8192,
                    }}
                    stencilProps={{
                      grid: true,
                    }}
                  />
                </Box>
              )}
              {editMode === 'perspective' && previewUrl && (
                <Box sx={{ position: 'relative', width: '100%', flex: '1 1 auto', minHeight: 0 }}>
                  <PerspectiveCropperCanvas
                    key={`perspective-${perspectiveKey}`}
                    ref={perspectiveCropperRef}
                    imageUrl={previewUrl}
                    file={selectedFile || new File([], 'existing-receipt.jpg', { type: 'image/jpeg' })}
                    onChange={onPerspectiveState}
                    onDragStop={onPerspectiveState}
                  />
                  {(perspectiveLoading || modeSwitching) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Preparing perspective view...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {editMode === 'free' && previewUrl && (
                <Box sx={{ position: 'relative', width: '100%', flex: '1 1 auto', minHeight: 0 }}>
                  <FreeCropCanvas
                    key={`free-${perspectiveKey}`}
                    ref={freeCropRef}
                    imageUrl={previewUrl}
                    file={selectedFile || new File([], 'existing-receipt.jpg', { type: 'image/jpeg' })}
                    onChange={onPerspectiveState}
                  />
                  {(perspectiveLoading || modeSwitching) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Processing...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
});

EditReceiptPreviewPanel.displayName = 'EditReceiptPreviewPanel';

export default EditReceiptPreviewPanel;
